/**
 * Phase 53.1 — Muhasebe (Accounting) ERP foundation.
 *
 * A single-file dashboard with tabbed sections. No automation yet — every
 * balance movement comes from a manual entry the operator files. Future
 * phases will layer POS / Sales / Inventory / Gold-brand hooks on top of
 * this without redrawing the UI.
 *
 *   Tabs
 *     • Genel Bakış        — headline cards + latest entries
 *     • Kasalar            — currency cash boxes
 *     • Gelir              — income entry form
 *     • Gider              — expense entry form (with categories)
 *     • Nakit Hareketleri  — free-form add / remove / transfer
 *     • Döviz Cüzdanları   — non-TRY cash aggregate view
 *     • Birikim            — savings accounts
 *     • Denetim            — accounting-only audit trail (from entries)
 *     • Raporlar           — placeholder
 *     • Ayarlar            — placeholder
 *
 * Everything is permission-gated: the sidebar entry hides the module when
 * the user lacks `accounting.view`; every mutating action further gates on
 * `accounting.create` (or `accounting.cancel` for the cancel button).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { useUsmPrefs } from "@/contexts/UsmPrefsContext";
import { PageHeader } from "./UsmLayout";
import { toast } from "sonner";
import { Wallet, Plus, Minus, ArrowRightLeft, X, RotateCcw, TrendingUp, TrendingDown, FileText, Settings, Coins, Banknote, Landmark, PiggyBank, ScrollText, LayoutGrid, Tag, Edit3, Sunrise, Sunset, Printer, Download } from "lucide-react";

const CURRENCY_FMT = {
  TRY: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 }),
  USD: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "USD", minimumFractionDigits: 2 }),
  EUR: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }),
  SAR: new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }),
  AED: new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }),
};
const fmt = (v, cur) => {
  const f = CURRENCY_FMT[cur];
  if (!f) return `${v}`;
  const s = f.format(Number(v || 0));
  return cur === "SAR" ? `${s} SAR` : cur === "AED" ? `${s} AED` : s;
};

const EXPENSE_LABELS = {
  rent: "Kira", electricity: "Elektrik", water: "Su", internet: "İnternet",
  natural_gas: "Doğalgaz", salary: "Maaş", tax: "Vergi", cargo: "Kargo",
  cleaning: "Temizlik", office: "Ofis", maintenance: "Bakım", other: "Diğer",
};

const KIND_LABELS = {
  income: "Gelir", expense: "Gider", adjust: "Düzeltme", transfer: "Transfer",
};

const SOURCE_LABELS = {
  pos_sale:  { label: "POS Satış",  cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  sale_void: { label: "Satış İadesi", cls: "bg-red-50 text-red-700 border border-red-200" },
  manual:    { label: "Manuel",     cls: "bg-stone-100 text-stone-700 border border-stone-200" },
};

function sourceBadge(e) {
  const key = e?.source || "manual";
  return SOURCE_LABELS[key] || SOURCE_LABELS.manual;
}

const TABS = [
  { key: "overview",  label: "Genel Bakış",       icon: LayoutGrid },
  { key: "cash",      label: "Kasalar",           icon: Banknote },
  { key: "income",    label: "Gelir",             icon: TrendingUp },
  { key: "expense",   label: "Gider",             icon: TrendingDown },
  { key: "movements", label: "Nakit Hareketleri", icon: ArrowRightLeft },
  { key: "wallets",   label: "Döviz Cüzdanları",  icon: Coins },
  { key: "savings",   label: "Birikim",           icon: PiggyBank },
  { key: "banks",     label: "Bankalar",          icon: Landmark },
  { key: "expense-categories", label: "Gider Kategorileri", icon: Tag },
  { key: "brands",    label: "Markalar",          icon: Tag },
  { key: "cash-open", label: "Kasa Açılış",       icon: Sunrise },
  { key: "cash-close", label: "Kasa Kapanış",     icon: Sunset },
  { key: "audit",     label: "Denetim",           icon: ScrollText },
  { key: "reports",   label: "Raporlar",          icon: FileText },
  { key: "settings",  label: "Ayarlar",           icon: Settings },
];

export default function AccountingPage() {
  const { hasPerm, isSuperAdmin } = useUsmAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "overview";
  const validKeys = new Set(TABS.map((t) => t.key));
  const [tab, setTab] = useState(validKeys.has(initialTab) ? initialTab : "overview");
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const canCreate = hasPerm("accounting.create");
  const canCancel = hasPerm("accounting.cancel") && isSuperAdmin;

  useEffect(() => {
    // Keep URL in sync so bookmarks / redirects like /usm/cash-close→?tab=cash-close work.
    if (tab !== "overview") setParams({ tab }, { replace: true });
    else if (params.get("tab")) setParams({}, { replace: true });
    /* eslint-disable-next-line */
  }, [tab]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([
        usmApi.get("/accounting/accounts"),
        usmApi.get("/accounting/entries?limit=200"),
      ]);
      setAccounts(a.data || []);
      setEntries(e.data || []);
    } catch { toast.error("Veri yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const byId = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const byCode = useMemo(() => Object.fromEntries(accounts.map((a) => [a.code, a])), [accounts]);
  const savingsAccounts = useMemo(() => accounts.filter((a) => a.kind === "savings"), [accounts]);
  const wallets = useMemo(() => accounts.filter((a) => a.kind === "cash" && a.currency !== "TRY"), [accounts]);

  return (
    <div data-testid="accounting-page">
      <PageHeader
        title="Muhasebe"
        icon={<Wallet size={22} className="text-[#C8A96A]" />}
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Muhasebe" }]}
      />

      {/* Tab bar — horizontal, scrollable on tablets/mobile */}
      <div className="border-b border-[#E7E5E4] mb-4 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`accounting-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={"px-3 sm:px-4 py-2.5 flex items-center gap-2 text-[11.5px] uppercase tracking-[0.15em] border-b-2 transition-colors " + (tab === t.key
                  ? "border-[#C8A96A] text-[#0A0A0A]"
                  : "border-transparent text-[#71717A] hover:text-[#0A0A0A]")}
              >
                <Icon size={14} /><span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-[#71717A]">Yükleniyor…</div>
      ) : (
        <>
          {tab === "overview"  && <OverviewTab accounts={accounts} entries={entries} />}
          {tab === "cash"      && <BalanceGrid accounts={accounts.filter((a) => a.kind === "cash" || a.kind === "pos" || a.kind === "bank")} title="Kasalar" testid="cash-grid" />}
          {tab === "income"    && <EntryForm mode="income"   accounts={accounts} canCreate={canCreate} onSaved={refresh} />}
          {tab === "expense"   && <EntryForm mode="expense"  accounts={accounts} canCreate={canCreate} onSaved={refresh} />}
          {tab === "movements" && <MovementsTab accounts={accounts} canCreate={canCreate} onSaved={refresh} />}
          {tab === "wallets"   && <BalanceGrid accounts={wallets} title="Döviz Cüzdanları" testid="wallets-grid" />}
          {tab === "savings"   && <SavingsTab accounts={savingsAccounts} canCreate={canCreate} onSaved={refresh} />}
          {tab === "brands"    && <BrandsTab canCreate={canCreate} />}
          {tab === "banks"     && <BanksTab canCreate={canCreate} onChanged={refresh} />}
          {tab === "expense-categories" && <ExpenseCategoriesTab canCreate={canCreate} />}
          {tab === "cash-open" && <CashOpeningTab canCreate={canCreate} />}
          {tab === "cash-close" && <CashClosingTab canCreate={canCreate} />}
          {tab === "audit"     && <AuditTab entries={entries} canCancel={canCancel} onCancelled={refresh} />}
          {tab === "reports"   && <ReportsTab />}
          {tab === "settings"  && <PlaceholderTab label="Ayarlar" note="Muhasebe modülünün özelleştirme seçenekleri ileride burada yer alacak." />}
        </>
      )}
    </div>
  );
}

// ── Overview ─────────────────────────────────────────────────────────────
function OverviewTab({ accounts, entries }) {
  const cash = accounts.filter((a) => a.kind === "cash");
  const other = accounts.filter((a) => a.kind === "pos" || a.kind === "bank" || a.kind === "savings");
  const recent = entries.slice(0, 12);
  return (
    <>
      <TodayRibbon />
      <div data-testid="overview-cash" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        {cash.map((a) => <BalanceCard key={a.id} account={a} />)}
      </div>
      <div data-testid="overview-other" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {other.filter((a) => a.kind !== "savings").map((a) => <BalanceCard key={a.id} account={a} accent />)}
      </div>
      <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Son İşlemler</h3>
      <EntriesTable entries={recent} compact />
    </>
  );
}

// Phase 53.5 — today's KPI strip. One backend aggregate, three currencies.
function TodayRibbon() {
  const { isSuperAdmin, employee } = useUsmAuth();
  const { prefs } = useUsmPrefs();
  const branchId = isSuperAdmin ? (prefs?.active_branch_id || "") : (employee?.branch_id || "");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!branchId) { setData(null); return; }
    usmApi.get(`/accounting/overview/today?branch_id=${encodeURIComponent(branchId)}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [branchId]);
  if (!branchId || !data) return null;
  const cur = "TRY";
  const inc = data.movements[cur].income; const exp = data.movements[cur].expense;
  const expected = data.expected[cur];
  const counted = data.closing?.counted_amount || 0;
  const diff = counted - expected;
  const net = inc - exp;
  return (
    <div data-testid="today-ribbon" className="mb-4">
      <p className="text-[10.5px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Bugünün Kasa Sağlığı · {data.business_date}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <RibbonCell testid="today-income" label="Bugün Gelir" value={fmt(inc, "TRY")} />
        <RibbonCell testid="today-expense" label="Bugün Gider" value={fmt(exp, "TRY")} />
        <RibbonCell testid="today-expected" label="Beklenen Kasa" value={fmt(expected, "TRY")} tone="accent" />
        <RibbonCell testid="today-counted" label="Sayılan Kasa" value={data.closing ? fmt(counted, "TRY") : "—"} />
        <RibbonCell testid="today-diff" label="Fark" value={data.closing ? (diff > 0 ? "+" : "") + fmt(diff, "TRY") : "—"} />
        <RibbonCell testid="today-net" label="Net Nakit" value={fmt(net, "TRY")} tone="accent" />
      </div>
    </div>
  );
}

function RibbonCell({ label, value, testid, tone }) {
  return (
    <div data-testid={testid} className={"px-3 py-2 border " + (tone === "accent" ? "border-[#C8A96A] bg-[#FBF5E8]" : "border-[#E7E5E4] bg-white")}>
      <p className="text-[9.5px] uppercase tracking-[0.2em] text-[#71717A]">{label}</p>
      <p className="font-finance text-base leading-tight">{value}</p>
    </div>
  );
}

function BalanceGrid({ accounts, title, testid }) {
  return (
    <>
      <h2 className="text-sm uppercase tracking-[0.2em] text-[#71717A] mb-3">{title}</h2>
      <div data-testid={testid} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {accounts.map((a) => <BalanceCard key={a.id} account={a} accent={a.kind !== "cash"} />)}
      </div>
    </>
  );
}

function BalanceCard({ account, accent }) {
  return (
    <div data-testid={`account-card-${account.code}`}
      className={"border p-4 " + (accent ? "bg-[#FBF5E8] border-[#E4CB88]" : "bg-white border-[#E7E5E4]")}>
      <div className="flex items-center gap-2 mb-1">
        <IconFor kind={account.kind} />
        <p className="text-[10.5px] uppercase tracking-[0.2em] text-[#71717A]">{account.label}</p>
      </div>
      <p data-testid={`account-balance-${account.code}`} className="font-finance text-xl text-[#0A0A0A]">
        {fmt(account.balance, account.currency)}
      </p>
      {account.updated_at && (
        <p className="text-[10px] text-[#A8A29E] mt-1">
          Güncellendi {new Date(account.updated_at).toLocaleString("tr-TR")}
        </p>
      )}
    </div>
  );
}

function IconFor({ kind }) {
  const cls = "text-[#C8A96A]";
  if (kind === "pos") return <Banknote size={14} className={cls} />;
  if (kind === "bank") return <Landmark size={14} className={cls} />;
  if (kind === "savings") return <PiggyBank size={14} className={cls} />;
  return <Coins size={14} className={cls} />;
}

// ── Entry form (used for income / expense) ───────────────────────────────
function EntryForm({ mode, accounts, canCreate, onSaved }) {
  const [account_id, setAcc] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDesc] = useState("");
  const [entry_date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  // Phase 53.6 — dynamic categories. Fetch from backend so freshly created
  // categories immediately appear here without a reload.
  const [dynamicCats, setDynamicCats] = useState([]);
  useEffect(() => {
    if (mode !== "expense") return;
    usmApi.get("/accounting/expense-categories")
      .then((r) => setDynamicCats(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDynamicCats([]));
  }, [mode]);

  const eligible = accounts.filter((a) => a.kind !== "savings"); // savings gets its own tab
  const valid = account_id && Number(amount) > 0 && (mode !== "expense" || category);

  const submit = async () => {
    setSaving(true);
    try {
      await usmApi.post("/accounting/entries", {
        kind: mode, account_id, amount: Number(amount),
        category: mode === "expense" ? category : undefined,
        description, entry_date,
      });
      toast.success(mode === "income" ? "Gelir kaydedildi" : "Gider kaydedildi");
      setAmount(""); setCategory(""); setDesc("");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kayıt başarısız");
    } finally { setSaving(false); }
  };

  if (!canCreate) return <p className="text-sm text-[#71717A]">Bu bölümü kullanma yetkiniz yok.</p>;

  return (
    <div data-testid={`entry-form-${mode}`} className="max-w-2xl bg-white border border-[#E7E5E4] p-6">
      <h2 className="font-heading text-lg mb-4">{mode === "income" ? "Manuel Gelir Ekle" : "Manuel Gider Ekle"}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Tarih">
          <input type="date" data-testid={`${mode}-date`} value={entry_date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Hesap *">
          <select data-testid={`${mode}-account`} value={account_id} onChange={(e) => setAcc(e.target.value)} className={inputCls}>
            <option value="">Seçin…</option>
            {eligible.map((a) => <option key={a.id} value={a.id}>{a.label} ({a.currency})</option>)}
          </select>
        </Field>
        <Field label="Tutar *">
          <input type="number" step="0.01" min="0" data-testid={`${mode}-amount`} value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder="0,00" />
        </Field>
        {mode === "expense" && (
          <Field label="Kategori *">
            <select data-testid="expense-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
              <option value="">Seçin…</option>
              {dynamicCats.length > 0
                ? dynamicCats.map((c) => <option key={c.code || c.id} value={c.code}>{c.label}</option>)
                : Object.entries(EXPENSE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <p className="text-[10px] text-[#71717A] mt-1">Yeni kategoriler <b>Gider Kategorileri</b> sekmesinden eklenir.</p>
          </Field>
        )}
        <Field label="Açıklama" className="sm:col-span-2">
          <input data-testid={`${mode}-description`} value={description} onChange={(e) => setDesc(e.target.value)} className={inputCls} placeholder="Kısa açıklama" />
        </Field>
      </div>
      <div className="mt-5">
        <button data-testid={`${mode}-submit`} onClick={submit} disabled={!valid || saving}
          className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

// ── Movements: free-form add / remove / transfer ────────────────────────
function MovementsTab({ accounts, canCreate, onSaved }) {
  const [op, setOp] = useState("add");   // add | remove | transfer
  const [account_id, setAcc] = useState("");
  const [counter_account_id, setCounter] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const valid = account_id && Number(amount) > 0 && (op !== "transfer" || (counter_account_id && counter_account_id !== account_id));

  const submit = async () => {
    setSaving(true);
    try {
      const kind = op === "add" ? "income" : op === "remove" ? "expense" : "transfer";
      const payload = { kind, account_id, amount: Number(amount), description };
      if (kind === "expense") payload.category = "other";
      if (kind === "transfer") payload.counter_account_id = counter_account_id;
      await usmApi.post("/accounting/entries", payload);
      toast.success("İşlem tamamlandı");
      setAmount(""); setDesc(""); setCounter("");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setSaving(false); }
  };

  if (!canCreate) return <p className="text-sm text-[#71717A]">Bu bölümü kullanma yetkiniz yok.</p>;

  return (
    <div data-testid="movements-tab" className="max-w-2xl bg-white border border-[#E7E5E4] p-6">
      <h2 className="font-heading text-lg mb-4">Nakit Hareketleri</h2>
      <div className="flex gap-2 mb-4">
        {[
          { k: "add", l: "Para Ekle", Icon: Plus },
          { k: "remove", l: "Para Çıkar", Icon: Minus },
          { k: "transfer", l: "Hesaplar Arası Transfer", Icon: ArrowRightLeft },
        ].map(({ k, l, Icon }) => (
          <button key={k} data-testid={`movement-op-${k}`} onClick={() => setOp(k)}
            className={"flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] border " + (op === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#E7E5E4]")}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={op === "transfer" ? "Kaynak Hesap *" : "Hesap *"}>
          <select data-testid="movement-account" value={account_id} onChange={(e) => setAcc(e.target.value)} className={inputCls}>
            <option value="">Seçin…</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.label} ({a.currency})</option>)}
          </select>
        </Field>
        {op === "transfer" && (
          <Field label="Hedef Hesap *">
            <select data-testid="movement-counter" value={counter_account_id} onChange={(e) => setCounter(e.target.value)} className={inputCls}>
              <option value="">Seçin…</option>
              {accounts.filter((a) => a.id !== account_id).map((a) => <option key={a.id} value={a.id}>{a.label} ({a.currency})</option>)}
            </select>
          </Field>
        )}
        <Field label="Tutar *">
          <input type="number" step="0.01" min="0" data-testid="movement-amount" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder="0,00" />
        </Field>
        <Field label="Açıklama" className="sm:col-span-2">
          <input data-testid="movement-description" value={description} onChange={(e) => setDesc(e.target.value)} className={inputCls} />
        </Field>
      </div>
      <div className="mt-5">
        <button data-testid="movement-submit" onClick={submit} disabled={!valid || saving}
          className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

// ── Savings ──────────────────────────────────────────────────────────────
function SavingsTab({ accounts, canCreate, onSaved }) {
  const [account_id, setAcc] = useState("");
  const [op, setOp] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [description, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const valid = account_id && Number(amount) > 0;
  const submit = async () => {
    setSaving(true);
    try {
      const kind = op === "deposit" ? "income" : "expense";
      const payload = { kind, account_id, amount: Number(amount), description };
      if (kind === "expense") payload.category = "other";
      await usmApi.post("/accounting/entries", payload);
      toast.success(op === "deposit" ? "Yatırım yapıldı" : "Çekim yapıldı");
      setAmount(""); setDesc("");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-6">
        {accounts.map((a) => <BalanceCard key={a.id} account={a} accent />)}
      </div>
      {!canCreate ? (
        <p className="text-sm text-[#71717A]">İşlem yapma yetkiniz yok.</p>
      ) : (
        <div data-testid="savings-form" className="max-w-2xl bg-white border border-[#E7E5E4] p-6">
          <h2 className="font-heading text-lg mb-4">Birikim İşlemi</h2>
          <div className="flex gap-2 mb-4">
            {[
              { k: "deposit", l: "Birikim Yatır", Icon: Plus },
              { k: "withdraw", l: "Birikim Çek", Icon: Minus },
            ].map(({ k, l, Icon }) => (
              <button key={k} data-testid={`savings-op-${k}`} onClick={() => setOp(k)}
                className={"flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] border " + (op === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#E7E5E4]")}>
                <Icon size={14} /> {l}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Birikim Hesabı *">
              <select data-testid="savings-account" value={account_id} onChange={(e) => setAcc(e.target.value)} className={inputCls}>
                <option value="">Seçin…</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.label} ({a.currency})</option>)}
              </select>
            </Field>
            <Field label="Tutar *">
              <input type="number" step="0.01" min="0" data-testid="savings-amount" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder="0,00" />
            </Field>
            <Field label="Açıklama" className="sm:col-span-2">
              <input data-testid="savings-description" value={description} onChange={(e) => setDesc(e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="mt-5">
            <button data-testid="savings-submit" onClick={submit} disabled={!valid || saving}
              className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Audit ────────────────────────────────────────────────────────────────
function AuditTab({ entries, canCancel, onCancelled }) {
  return (
    <>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm uppercase tracking-[0.2em] text-[#71717A]">Muhasebe Denetim Kaydı</h2>
        <span className="text-[10.5px] text-[#A8A29E]">Toplam {entries.length} kayıt</span>
      </div>
      <EntriesTable entries={entries} canCancel={canCancel} onCancelled={onCancelled} />
    </>
  );
}

function EntriesTable({ entries, canCancel, onCancelled, compact }) {
  const [busy, setBusy] = useState(null);
  const cancel = async (id) => {
    if (!window.confirm("Bu kaydı iptal etmek istediğinize emin misiniz?")) return;
    setBusy(id);
    try {
      await usmApi.post(`/accounting/entries/${id}/cancel`);
      toast.success("Kayıt iptal edildi");
      onCancelled?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "İptal başarısız"); }
    finally { setBusy(null); }
  };
  if (!entries.length) return <p className="text-sm text-[#71717A] py-8 text-center">Kayıt yok.</p>;
  return (
    <div className="overflow-x-auto bg-white border border-[#E7E5E4]">
      <table className="min-w-full text-sm">
        <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
          <tr>
            <th className="text-left px-3 py-2">Tarih</th>
            <th className="text-left px-3 py-2">Tür</th>
            <th className="text-left px-3 py-2">Kaynak</th>
            <th className="text-left px-3 py-2">Hesap</th>
            <th className="text-right px-3 py-2">Tutar</th>
            <th className="text-left px-3 py-2">Referans</th>
            <th className="text-left px-3 py-2">Açıklama</th>
            {!compact && <th className="text-left px-3 py-2">Kullanıcı</th>}
            <th className="text-left px-3 py-2">Durum</th>
            {canCancel && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const cancelled = e.status === "cancelled";
            const sign = (e.kind === "expense") ? "−" : (e.kind === "income" || e.kind === "adjust") ? "+" : "";
            const acctLabel = e.account?.label || "—";
            const src = sourceBadge(e);
            const isAuto = !!(e.is_auto || (e.source && e.source !== "manual"));
            return (
              <tr key={e.id} data-testid={`entry-row-${e.id}`}
                className={"border-t border-[#F5F5F4] " + (cancelled ? "text-[#A8A29E] line-through" : "")}>
                <td className="px-3 py-2 whitespace-nowrap font-mono text-[11.5px]">{new Date(e.created_at).toLocaleString("tr-TR")}</td>
                <td className="px-3 py-2">
                  <span className={"inline-block px-2 py-0.5 text-[10.5px] uppercase tracking-[0.15em] " + kindBadge(e.kind)}>{KIND_LABELS[e.kind]}</span>
                  {e.category && <span className="ml-2 text-[10.5px] text-[#71717A]">{EXPENSE_LABELS[e.category] || e.category}</span>}
                </td>
                <td className="px-3 py-2">
                  <span data-testid={`entry-source-${e.id}`} className={"inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] " + src.cls}>{src.label}</span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {acctLabel}
                  {e.kind === "transfer" && e.counter_account && <span className="text-[#71717A]"> → {e.counter_account.label}</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  <span className={e.kind === "expense" ? "text-red-600" : e.kind === "income" ? "text-emerald-600" : ""}>
                    {sign}{fmt(e.amount, e.currency)}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-[11.5px] text-[#52525B]">{e.source_ref || <span className="text-[#A8A29E]">—</span>}</td>
                <td className="px-3 py-2 text-[#52525B]">{e.description || <span className="text-[#A8A29E]">—</span>}</td>
                {!compact && <td className="px-3 py-2 text-[#52525B]">{e.created_by_username || "—"}</td>}
                <td className="px-3 py-2">
                  <span className={cancelled ? "text-red-600" : "text-emerald-600"}>{cancelled ? "İptal" : "Aktif"}</span>
                </td>
                {canCancel && (
                  <td className="px-3 py-2 text-right">
                    {!cancelled && !isAuto && (
                      <button data-testid={`entry-cancel-${e.id}`} onClick={() => cancel(e.id)} disabled={busy === e.id}
                        className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:underline">
                        <RotateCcw size={12} /> {busy === e.id ? "…" : "İptal"}
                      </button>
                    )}
                    {!cancelled && isAuto && (
                      <span className="text-[10px] text-[#A8A29E] italic" title="Bu kayıt satış üzerinden otomatik oluşturulmuştur. İptal için satışı iptal ediniz.">Kilit</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function kindBadge(k) {
  if (k === "income") return "bg-emerald-50 text-emerald-700 border border-emerald-200";
  if (k === "expense") return "bg-red-50 text-red-700 border border-red-200";
  if (k === "transfer") return "bg-blue-50 text-blue-700 border border-blue-200";
  return "bg-stone-100 text-stone-700 border border-stone-200";
}

// ── Cash Opening + Closing (Phase 53.4) ─────────────────────────────────
// Reuses accounting.entries as the single source of truth for the day's
// income/expense; the opening row is a per-branch, per-date base balance.
function useBranchScope() {
  const { isSuperAdmin, employee } = useUsmAuth();
  const { prefs, setActiveBranch } = useUsmPrefs();
  const [branches, setBranches] = useState([]);
  useEffect(() => { usmApi.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, []);
  const branchId = isSuperAdmin ? (prefs?.active_branch_id || "") : (employee?.branch_id || "");
  return { branches, branchId, setBranchId: setActiveBranch, isSuperAdmin };
}

function DayBranchBar({ date, setDate, branchId, setBranchId, branches, isSuperAdmin }) {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Tarih</label>
        <input type="date" data-testid="cash-date" value={date} onChange={(e) => setDate(e.target.value)} className="px-3 py-2 border border-[#E7E5E4] text-sm" />
      </div>
      {isSuperAdmin && (
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Şube</label>
          <select data-testid="cash-branch" value={branchId} onChange={(e) => setBranchId(e.target.value || null)}
            className="px-3 py-2 border border-[#E7E5E4] text-sm bg-white min-w-[220px]">
            <option value="">Seçin…</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function CashOpeningTab({ canCreate }) {
  const { branches, branchId, setBranchId, isSuperAdmin } = useBranchScope();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [row, setRow] = useState(null);
  const [tr, setTr] = useState(""); const [usd, setUsd] = useState(""); const [eur, setEur] = useState(""); const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    if (!branchId) { setRow(null); return; }
    setLoading(true);
    try {
      const r = await usmApi.get(`/accounting/openings?branch_id=${encodeURIComponent(branchId)}&business_date=${date}`);
      setRow(r.data);
      setTr(r.data.opening_try || ""); setUsd(r.data.opening_usd || ""); setEur(r.data.opening_eur || ""); setNote(r.data.note || "");
    } catch { toast.error("Kasa açılışı yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [branchId, date]);

  const submit = async () => {
    if (!branchId) { toast.error("Şube seçin"); return; }
    setSaving(true);
    try {
      await usmApi.post("/accounting/openings", {
        branch_id: branchId, business_date: date,
        opening_try: Number(tr || 0), opening_usd: Number(usd || 0), opening_eur: Number(eur || 0), note,
      });
      toast.success(row?.exists ? "Kasa açılışı güncellendi" : "Kasa açılışı oluşturuldu");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="cash-opening-tab">
      <DayBranchBar date={date} setDate={setDate} branchId={branchId} setBranchId={setBranchId} branches={branches} isSuperAdmin={isSuperAdmin} />
      <div className="max-w-2xl bg-white border border-[#E7E5E4] p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg">Kasa Açılışı</h2>
          {row?.id && <span className="text-[10.5px] uppercase tracking-[0.15em] px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">Mevcut · Düzenle</span>}
        </div>
        {loading ? <p className="text-sm text-[#71717A]">Yükleniyor…</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Açılış TRY"><input type="number" min="0" step="0.01" data-testid="open-try" value={tr} onChange={(e) => setTr(e.target.value)} className={inputCls} /></Field>
            <Field label="Açılış USD"><input type="number" min="0" step="0.01" data-testid="open-usd" value={usd} onChange={(e) => setUsd(e.target.value)} className={inputCls} /></Field>
            <Field label="Açılış EUR"><input type="number" min="0" step="0.01" data-testid="open-eur" value={eur} onChange={(e) => setEur(e.target.value)} className={inputCls} /></Field>
            <Field label="Not" className="sm:col-span-3"><input data-testid="open-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Opsiyonel" /></Field>
          </div>
        )}
        <div className="mt-5">
          {canCreate ? (
            <button data-testid="open-submit" onClick={submit} disabled={saving || !branchId}
              className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
              {saving ? "Kaydediliyor…" : (row?.exists ? "Güncelle" : "Kaydet")}
            </button>
          ) : <p className="text-sm text-[#71717A]">İşlem yetkiniz yok.</p>}
        </div>
        <p className="text-[10.5px] text-[#71717A] mt-3">
          Aynı gün için ikinci bir açılış yapılamaz; kayıt zaten varsa üzerine yazılır.
        </p>
      </div>
    </div>
  );
}

function CashClosingTab({ canCreate }) {
  const { isSuperAdmin } = useUsmAuth();
  const { branches, branchId, setBranchId, isSuperAdmin: superOnly } = useBranchScope();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [flow, setFlow] = useState(null);
  const [countedTry, setCTry] = useState(""); const [countedUsd, setCUsd] = useState(""); const [countedEur, setCEur] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    if (!branchId) { setFlow(null); return; }
    try {
      const r = await usmApi.get(`/accounting/cash-flow?branch_id=${encodeURIComponent(branchId)}&business_date=${date}`);
      setFlow(r.data);
    } catch { toast.error("Nakit akışı yüklenemedi"); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [branchId, date]);

  const diff = useMemo(() => {
    if (!flow) return null;
    return {
      TRY: Number(countedTry || 0) - flow.expected.TRY,
      USD: Number(countedUsd || 0) - flow.expected.USD,
      EUR: Number(countedEur || 0) - flow.expected.EUR,
    };
  }, [flow, countedTry, countedUsd, countedEur]);

  const submit = async () => {
    if (!branchId) { toast.error("Şube seçin"); return; }
    setSaving(true);
    try {
      // Uses the existing cash_close backend for durable storage.
      // (Auto-lock is enforced there; reopening requires Super Admin.)
      await usmApi.post("/cash-close", {
        branch_id: branchId,
        counted_amount: Number(countedTry || 0),
        counted_amount_usd: Number(countedUsd || 0),
        counted_amount_eur: Number(countedEur || 0),
        expected_amount: flow?.expected?.TRY || 0,
        note,
        business_date: date,
      });
      toast.success("Kasa kapanışı kaydedildi");
      setCTry(""); setCUsd(""); setCEur(""); setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="cash-closing-tab">
      <DayBranchBar date={date} setDate={setDate} branchId={branchId} setBranchId={setBranchId} branches={branches} isSuperAdmin={superOnly} />
      {!flow ? (
        <p className="text-sm text-[#71717A]">Şube ve tarih seçin.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            {["TRY", "USD", "EUR"].map((cur) => (
              <div key={cur} data-testid={`expected-${cur}`} className="border border-[#E7E5E4] bg-white p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Beklenen {cur}</p>
                <p className="font-finance text-xl">{fmt(flow.expected[cur], cur)}</p>
                <p className="text-[10.5px] text-[#71717A] mt-1">
                  Açılış {fmt(flow.opening[cur], cur)} · Gelir {fmt(flow.movements[cur].income, cur)} · Gider {fmt(flow.movements[cur].expense, cur)}
                </p>
              </div>
            ))}
          </div>
          <div className="max-w-3xl bg-white border border-[#E7E5E4] p-6">
            <h3 className="font-heading text-lg mb-4">Sayılan Nakit</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Sayılan TRY"><input type="number" min="0" step="0.01" data-testid="counted-try" value={countedTry} onChange={(e) => setCTry(e.target.value)} className={inputCls} /></Field>
              <Field label="Sayılan USD"><input type="number" min="0" step="0.01" data-testid="counted-usd" value={countedUsd} onChange={(e) => setCUsd(e.target.value)} className={inputCls} /></Field>
              <Field label="Sayılan EUR"><input type="number" min="0" step="0.01" data-testid="counted-eur" value={countedEur} onChange={(e) => setCEur(e.target.value)} className={inputCls} /></Field>
            </div>
            {diff && (
              <div data-testid="cash-diff" className="mt-4 grid grid-cols-3 gap-3">
                {["TRY", "USD", "EUR"].map((cur) => {
                  const v = diff[cur];
                  const status = Math.abs(v) < 0.005 ? "Denk" : v > 0 ? "Fazla" : "Eksik";
                  const cls = Math.abs(v) < 0.005 ? "text-emerald-700" : v > 0 ? "text-amber-700" : "text-red-700";
                  return (
                    <div key={cur} className="border border-[#E7E5E4] p-3 text-center">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Fark {cur}</p>
                      <p className={"font-mono font-semibold " + cls}>{v > 0 ? "+" : ""}{fmt(v, cur)}</p>
                      <p className={"text-[10.5px] uppercase tracking-[0.15em] mt-0.5 " + cls}>{status}</p>
                    </div>
                  );
                })}
              </div>
            )}
            <Field label="Not" className="mt-3">
              <input data-testid="close-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
            </Field>
            <div className="mt-5">
              {canCreate ? (
                <button data-testid="close-submit" onClick={submit} disabled={saving || countedTry === ""}
                  className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
                  {saving ? "Kaydediliyor…" : "Kapanışı Kaydet"}
                </button>
              ) : <p className="text-sm text-[#71717A]">İşlem yetkiniz yok.</p>}
              {isSuperAdmin && <p className="text-[10.5px] text-[#71717A] mt-2">Süper yönetici tekrar açış talepleri denetim kaydı üretir.</p>}
            </div>
          </div>
        </>
      )}
      {branchId && <CashCloseHistory branchId={branchId} />}
    </div>
  );
}

function CashCloseHistory({ branchId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    usmApi.get(`/accounting/cash-close-history?branch_id=${encodeURIComponent(branchId)}&limit=50`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [branchId]);
  if (loading) return null;
  return (
    <div data-testid="cash-close-history" className="mt-6">
      <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Geçmiş Kapanışlar</h3>
      {!rows.length ? (
        <p className="text-sm text-[#A8A29E]">Henüz kapanış kaydı yok.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#E7E5E4]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
              <tr>
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-left px-3 py-2">Şube</th>
                <th className="text-left px-3 py-2">Kapatan</th>
                <th className="text-right px-3 py-2">Beklenen</th>
                <th className="text-right px-3 py-2">Sayılan</th>
                <th className="text-right px-3 py-2">Fark</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">Not</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const exp = Number(r.expected_amount || 0);
                const cnt = Number(r.counted_amount || 0);
                const diff = cnt - exp;
                const stat = Math.abs(diff) < 0.005 ? "Denk" : diff > 0 ? "Fazla" : "Eksik";
                const cls = Math.abs(diff) < 0.005 ? "text-emerald-700" : diff > 0 ? "text-amber-700" : "text-red-700";
                return (
                  <tr key={r.id} data-testid={`close-row-${r.id}`} className="border-t border-[#F5F5F4]">
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-[11.5px]">{r.business_date || (r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">{r.branch_name || "—"}</td>
                    <td className="px-3 py-2 text-[#52525B]">{r.closed_by_name || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(exp, "TRY")}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(cnt, "TRY")}</td>
                    <td className={"px-3 py-2 text-right font-mono " + cls}>{diff > 0 ? "+" : ""}{fmt(diff, "TRY")}</td>
                    <td className={"px-3 py-2 " + cls}>{stat}</td>
                    <td className="px-3 py-2 text-[#52525B] truncate max-w-[220px]">{r.note || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/usm/cash-close/${r.id}`} className="text-[11px] text-[#0A0A0A] hover:text-[#C8A96A]">Detay</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function PlaceholderTab({ label, note }) {
  return (
    <div data-testid="accounting-placeholder" className="bg-white border border-dashed border-[#E7E5E4] p-12 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-[#FBF5E8] text-[#C8A96A] flex items-center justify-center mb-4">
        <FileText size={22} />
      </div>
      <h3 className="font-heading text-lg mb-1">{label}</h3>
      <p className="text-sm text-[#71717A] max-w-md mx-auto">{note}</p>
    </div>
  );
}

// ── Brands (Phase 53.3) ──────────────────────────────────────────────────
// Master data + live stats for the Gold Brand Engine. Brands are dynamic,
// created only inside Accounting, and immediately surface everywhere else
// (currently: Gold Inventory New/Edit form; future: Diamond / Watch / etc.
// via the `category` field on the brand row).
function BrandsTab({ canCreate }) {
  const { isSuperAdmin } = useUsmAuth();
  const { prefs, setActiveBranch } = useUsmPrefs();
  const [brands, setBrands] = useState([]);
  const [stats, setStats] = useState([]);
  const [rate, setRate] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);          // brand row or "new"
  const [movementsFor, setMovementsFor] = useState(null); // stats row

  const branchId = isSuperAdmin ? (prefs?.active_branch_id || "") : "";

  const refresh = async () => {
    setLoading(true);
    try {
      const qs = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : "";
      const [b, s] = await Promise.all([
        usmApi.get("/accounting/brands"),
        usmApi.get(`/accounting/brands/stats${qs}`),
      ]);
      setBrands(b.data || []);
      setStats(s.data || []);
    } catch { toast.error("Marka verileri yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [branchId]);
  useEffect(() => { if (isSuperAdmin) usmApi.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, [isSuperAdmin]);

  // TCMB USD rate for USD equivalents (reuse existing public endpoint).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const r = await fetch(`${base}/api/rates`);
        if (!alive || !r.ok) return;
        const d = await r.json();
        const usd = (d.currencies || []).find((c) => c.code === "USD");
        if (alive) setRate(usd?.forex_selling || usd?.display_sell || null);
      } catch { /* offline */ }
    })();
    return () => { alive = false; };
  }, []);

  const scopeLabel = useMemo(() => {
    if (!isSuperAdmin) return null;
    if (!branchId) return "Tüm şubeler";
    return branches.find((b) => b.id === branchId)?.name || "—";
  }, [isSuperAdmin, branchId, branches]);

  return (
    <div data-testid="brands-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="text-sm uppercase tracking-[0.2em] text-[#71717A]">Markalar</h2>
        <span className="text-[10.5px] text-[#A8A29E]">Toplam {brands.length} marka</span>
        <div className="ml-auto flex items-center gap-2">
          {isSuperAdmin && (
            <select
              data-testid="brands-branch-filter"
              value={branchId}
              onChange={(e) => setActiveBranch(e.target.value || null)}
              className="px-3 py-2 border border-[#E7E5E4] text-sm bg-white"
            >
              <option value="">Tüm şubeler</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {canCreate && (
            <button data-testid="brand-new-btn" onClick={() => setEditing({ mode: "new", data: { name: "", description: "", status: "active" } })}
              className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-4 py-2">
              <Plus size={14} /> Yeni Marka
            </button>
          )}
        </div>
      </div>
      {isSuperAdmin && scopeLabel && (
        <p className="text-[10.5px] text-[#71717A] mb-3">Kapsam: <span className="text-[#0A0A0A]">{scopeLabel}</span></p>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-[#71717A]">Yükleniyor…</div>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#E7E5E4]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
              <tr>
                <th className="text-left px-3 py-2">Marka</th>
                <th className="text-right px-3 py-2">Ürün Adedi</th>
                <th className="text-right px-3 py-2">Toplam Gram</th>
                <th className="text-right px-3 py-2">Alış (TRY)</th>
                <th className="text-right px-3 py-2">Satış (TRY)</th>
                <th className="text-right px-3 py-2">USD Karşılığı</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">Son Güncelleme</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {stats.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-[#A8A29E] text-sm">
                  {canCreate ? "Henüz marka yok. Yukarıdaki 'Yeni Marka' butonu ile ekleyin." : "Henüz marka yok."}
                </td></tr>
              ) : stats.map((s) => (
                <tr key={s.brand_id} data-testid={`brand-row-${s.brand_id}`} className="border-t border-[#F5F5F4]">
                  <td className="px-3 py-2">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[10.5px] text-[#A8A29E] uppercase tracking-[0.15em]">{s.category || "gold"}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{s.product_count}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.total_weight_grams.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} g</td>
                  <td className="px-3 py-2 text-right font-mono">{s.total_purchase_try == null ? <span className="text-[#A8A29E]">—</span> : fmt(s.total_purchase_try, "TRY")}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(s.total_sale_try, "TRY")}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#52525B]">
                    {rate ? "$" + (s.total_sale_try / rate).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={"inline-block px-2 py-0.5 text-[10.5px] uppercase tracking-[0.15em] " + (s.status === "active"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-stone-100 text-stone-600 border border-stone-200")}>{s.status === "active" ? "Aktif" : "Pasif"}</span>
                  </td>
                  <td className="px-3 py-2 text-[11.5px] text-[#52525B]">{s.last_updated ? new Date(s.last_updated).toLocaleString("tr-TR") : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {canCreate && (
                      <button data-testid={`brand-edit-${s.brand_id}`}
                        onClick={() => setEditing({ mode: "edit", data: brands.find((b) => b.id === s.brand_id) })}
                        className="inline-flex items-center gap-1 text-[11px] text-[#0A0A0A] hover:text-[#C8A96A]">
                        <Edit3 size={12} /> Düzenle
                      </button>
                    )}
                    <button data-testid={`brand-movements-${s.brand_id}`}
                      onClick={() => setMovementsFor(s)}
                      className="ml-3 inline-flex items-center gap-1 text-[11px] text-[#0A0A0A] hover:text-[#C8A96A]">
                      <ScrollText size={12} /> Hareketler
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <BrandEditor mode={editing.mode} data={editing.data} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {movementsFor && <BrandMovementsModal stats={movementsFor} onClose={() => setMovementsFor(null)} />}
    </div>
  );
}

function BrandMovementsModal({ stats, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    usmApi.get(`/accounting/brands/${stats.brand_id}/movements?limit=100`)
      .then((r) => setData(r.data)).catch(() => setData({ items: [], audit: [] }));
  }, [stats.brand_id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div data-testid="brand-movements-modal" className="bg-white w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg">{stats.name} · Hareket Geçmişi</h2>
            <p className="text-[10.5px] text-[#71717A] uppercase tracking-[0.15em] mt-0.5">{stats.category || "gold"}</p>
          </div>
          <button onClick={onClose} className="text-[#71717A]"><X size={18} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {!data ? <p className="text-sm text-[#71717A]">Yükleniyor…</p> : (
            <>
              <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Bu Markaya Bağlı Ürünler ({data.items.length})</h3>
              {data.items.length === 0 ? (
                <p className="text-sm text-[#A8A29E] mb-6">Şu an bu markaya bağlı ürün yok.</p>
              ) : (
                <div className="overflow-x-auto bg-white border border-[#E7E5E4] mb-6">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                      <tr>
                        <th className="text-left px-3 py-2">Stok Kodu</th>
                        <th className="text-left px-3 py-2">Ürün</th>
                        <th className="text-left px-3 py-2">Materyal</th>
                        <th className="text-right px-3 py-2">Ağırlık</th>
                        <th className="text-left px-3 py-2">Durum</th>
                        <th className="text-left px-3 py-2">Son Güncelleme</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((it) => (
                        <tr key={it.id} className="border-t border-[#F5F5F4]">
                          <td className="px-3 py-2 font-mono text-[11.5px]">{it.stock_code}</td>
                          <td className="px-3 py-2">{it.name}</td>
                          <td className="px-3 py-2 text-[11.5px]">{it.material || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono">{it.weight_grams != null ? `${it.weight_grams} g` : "—"}</td>
                          <td className="px-3 py-2 text-[11.5px]">{it.status || "—"}</td>
                          <td className="px-3 py-2 text-[11.5px] text-[#52525B]">{it.updated_at ? new Date(it.updated_at).toLocaleString("tr-TR") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Denetim Kayıtları ({data.audit.length})</h3>
              {data.audit.length === 0 ? (
                <p className="text-sm text-[#A8A29E]">Denetim kaydı henüz yok. Yeni ürün ekleyip düzenlediğinizde buraya işlenir.</p>
              ) : (
                <div className="overflow-x-auto bg-white border border-[#E7E5E4]">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                      <tr>
                        <th className="text-left px-3 py-2">Tarih</th>
                        <th className="text-left px-3 py-2">Eylem</th>
                        <th className="text-left px-3 py-2">Kullanıcı</th>
                        <th className="text-left px-3 py-2">Referans</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.audit.map((a, i) => (
                        <tr key={a.id || i} className="border-t border-[#F5F5F4]">
                          <td className="px-3 py-2 whitespace-nowrap font-mono text-[11.5px]">{a.at ? new Date(a.at).toLocaleString("tr-TR") : "—"}</td>
                          <td className="px-3 py-2">{a.action}</td>
                          <td className="px-3 py-2 text-[#52525B]">{a.actor_username || a.actor_id || "—"}</td>
                          <td className="px-3 py-2 font-mono text-[11.5px] text-[#52525B]">{a.target_id ? String(a.target_id).slice(0, 12) + "…" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BrandEditor({ mode, data, onClose, onSaved }) {
  const [name, setName] = useState(data?.name || "");
  const [description, setDesc] = useState(data?.description || "");
  const [status, setStatus] = useState(data?.status || "active");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const n = (name || "").trim();
    if (!n) { toast.error("Marka adı gerekli"); return; }
    setSaving(true);
    try {
      if (mode === "new") {
        await usmApi.post("/accounting/brands", { name: n, description, status });
        toast.success("Marka oluşturuldu");
      } else {
        await usmApi.patch(`/accounting/brands/${data.id}`, { name: n, description, status });
        toast.success("Marka güncellendi");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div data-testid="brand-editor" className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h2 className="font-heading text-lg">{mode === "new" ? "Yeni Marka" : "Marka Düzenle"}</h2>
          <button onClick={onClose} className="text-[#71717A]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Marka Adı *">
            <input data-testid="brand-input-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Örn: Uzungol Design" />
          </Field>
          <Field label="Açıklama (opsiyonel)">
            <input data-testid="brand-input-description" value={description} onChange={(e) => setDesc(e.target.value)} className={inputCls} placeholder="Kısa notlar" />
          </Field>
          <Field label="Durum">
            <select data-testid="brand-input-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-[#E7E5E4] flex justify-end gap-3">
          <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9]">İptal</button>
          <button data-testid="brand-submit" onClick={submit} disabled={saving || !name.trim()}
            className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Field helper ─────────────────────────────────────────────────────────
function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[10.5px] uppercase tracking-[0.15em] text-[#71717A] mb-1">{label}</label>
      {children}
    </div>
  );
}
const inputCls = "w-full px-3 py-2 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";

// ── Phase 53.6 — Dynamic Banks ────────────────────────────────────────────
function BanksTab({ canCreate, onChanged }) {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const refresh = async () => {
    setLoading(true);
    try {
      const r = await usmApi.get("/accounting/banks");
      setBanks(r.data || []);
    } catch { toast.error("Bankalar yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  return (
    <div data-testid="banks-tab">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-sm uppercase tracking-[0.2em] text-[#71717A]">Bankalar</h2>
        <span className="text-[10.5px] text-[#A8A29E]">Toplam {banks.length} banka</span>
        {canCreate && (
          <button data-testid="bank-new-btn"
            onClick={() => setEditing({ mode: "new", data: { name: "", description: "", status: "active", currency: "TRY" } })}
            className="ml-auto inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-4 py-2">
            <Plus size={14} /> Yeni Banka
          </button>
        )}
      </div>
      {loading ? <div className="py-16 text-center text-sm text-[#71717A]">Yükleniyor…</div> : (
        <div className="overflow-x-auto bg-white border border-[#E7E5E4]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
              <tr>
                <th className="text-left px-3 py-2">Banka</th>
                <th className="text-left px-3 py-2">Para Birimi</th>
                <th className="text-right px-3 py-2">Bakiye</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {banks.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-[#A8A29E] text-sm">Henüz banka yok.</td></tr>
              ) : banks.map((b) => (
                <tr key={b.id} data-testid={`bank-row-${b.id}`} className="border-t border-[#F5F5F4]">
                  <td className="px-3 py-2">
                    <div className="font-medium">{b.name}</div>
                    {b.description && <div className="text-[10.5px] text-[#A8A29E]">{b.description}</div>}
                  </td>
                  <td className="px-3 py-2 text-[11.5px]">{b.account_currency || b.currency || "TRY"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(b.balance || 0, b.account_currency || b.currency || "TRY")}</td>
                  <td className="px-3 py-2">
                    <span className={"inline-block px-2 py-0.5 text-[10.5px] uppercase tracking-[0.15em] " + (b.status === "active"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-stone-100 text-stone-600 border border-stone-200")}>{b.status === "active" ? "Aktif" : "Pasif"}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canCreate && (
                      <button data-testid={`bank-edit-${b.id}`}
                        onClick={() => setEditing({ mode: "edit", data: b })}
                        className="inline-flex items-center gap-1 text-[11px] text-[#0A0A0A] hover:text-[#C8A96A]">
                        <Edit3 size={12} /> Düzenle
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <BankEditor mode={editing.mode} data={editing.data} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); onChanged?.(); }} />
      )}
    </div>
  );
}

function BankEditor({ mode, data, onClose, onSaved }) {
  const [name, setName] = useState(data?.name || "");
  const [description, setDesc] = useState(data?.description || "");
  const [status, setStatus] = useState(data?.status || "active");
  const [currency, setCurrency] = useState(data?.currency || "TRY");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const n = (name || "").trim();
    if (!n) { toast.error("Banka adı gerekli"); return; }
    setSaving(true);
    try {
      if (mode === "new") {
        await usmApi.post("/accounting/banks", { name: n, description, status, currency });
        toast.success("Banka oluşturuldu");
      } else {
        await usmApi.patch(`/accounting/banks/${data.id}`, { name: n, description, status });
        toast.success("Banka güncellendi");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div data-testid="bank-editor" className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h2 className="font-heading text-lg">{mode === "new" ? "Yeni Banka" : "Banka Düzenle"}</h2>
          <button onClick={onClose} className="text-[#71717A]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Banka Adı *">
            <input data-testid="bank-input-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Örn: Garanti BBVA" />
          </Field>
          <Field label="Açıklama (opsiyonel)">
            <input data-testid="bank-input-description" value={description} onChange={(e) => setDesc(e.target.value)} className={inputCls} placeholder="IBAN, hesap no vb." />
          </Field>
          {mode === "new" && (
            <Field label="Para Birimi">
              <select data-testid="bank-input-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
                {["TRY", "USD", "EUR", "SAR", "AED"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          )}
          <Field label="Durum">
            <select data-testid="bank-input-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-[#E7E5E4] flex justify-end gap-3">
          <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9]">İptal</button>
          <button data-testid="bank-submit" onClick={submit} disabled={saving || !name.trim()}
            className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Phase 53.6 — Dynamic Expense Categories ──────────────────────────────
function ExpenseCategoriesTab({ canCreate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const refresh = async () => {
    setLoading(true);
    try {
      const r = await usmApi.get("/accounting/expense-categories");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error("Kategoriler yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  return (
    <div data-testid="expense-categories-tab">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-sm uppercase tracking-[0.2em] text-[#71717A]">Gider Kategorileri</h2>
        <span className="text-[10.5px] text-[#A8A29E]">Toplam {rows.length}</span>
        {canCreate && (
          <button data-testid="expcat-new-btn"
            onClick={() => setEditing({ mode: "new", data: { label: "", status: "active" } })}
            className="ml-auto inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-4 py-2">
            <Plus size={14} /> Yeni Kategori
          </button>
        )}
      </div>
      {loading ? <div className="py-16 text-center text-sm text-[#71717A]">Yükleniyor…</div> : (
        <div className="overflow-x-auto bg-white border border-[#E7E5E4]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
              <tr>
                <th className="text-left px-3 py-2">Ad</th>
                <th className="text-left px-3 py-2">Kod</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">Sistem</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-[#A8A29E] text-sm">Kayıt yok.</td></tr>
              ) : rows.map((c) => (
                <tr key={c.id || c.code} data-testid={`expcat-row-${c.code}`} className="border-t border-[#F5F5F4]">
                  <td className="px-3 py-2 font-medium">{c.label}</td>
                  <td className="px-3 py-2 font-mono text-[11.5px] text-[#52525B]">{c.code}</td>
                  <td className="px-3 py-2">
                    <span className={"inline-block px-2 py-0.5 text-[10.5px] uppercase tracking-[0.15em] " + (c.status === "active"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-stone-100 text-stone-600 border border-stone-200")}>{c.status === "active" ? "Aktif" : "Pasif"}</span>
                  </td>
                  <td className="px-3 py-2 text-[11.5px] text-[#52525B]">{c.is_system ? "Sistem" : "Özel"}</td>
                  <td className="px-3 py-2 text-right">
                    {canCreate && c.id && (
                      <button data-testid={`expcat-edit-${c.code}`}
                        onClick={() => setEditing({ mode: "edit", data: c })}
                        className="inline-flex items-center gap-1 text-[11px] text-[#0A0A0A] hover:text-[#C8A96A]">
                        <Edit3 size={12} /> Düzenle
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <ExpCatEditor mode={editing.mode} data={editing.data} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

function ExpCatEditor({ mode, data, onClose, onSaved }) {
  const [label, setLabel] = useState(data?.label || "");
  const [status, setStatus] = useState(data?.status || "active");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const n = (label || "").trim();
    if (!n) { toast.error("Kategori adı gerekli"); return; }
    setSaving(true);
    try {
      if (mode === "new") {
        await usmApi.post("/accounting/expense-categories", { label: n, status });
        toast.success("Kategori oluşturuldu");
      } else {
        await usmApi.patch(`/accounting/expense-categories/${data.id}`, { label: n, status });
        toast.success("Kategori güncellendi");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div data-testid="expcat-editor" className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h2 className="font-heading text-lg">{mode === "new" ? "Yeni Kategori" : "Kategori Düzenle"}</h2>
          <button onClick={onClose} className="text-[#71717A]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Kategori Adı *">
            <input data-testid="expcat-input-label" value={label} onChange={(e) => setLabel(e.target.value)} className={inputCls} placeholder="Örn: Personel Yemek" />
          </Field>
          <Field label="Durum">
            <select data-testid="expcat-input-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-[#E7E5E4] flex justify-end gap-3">
          <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9]">İptal</button>
          <button data-testid="expcat-submit" onClick={submit} disabled={saving || !label.trim()}
            className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Phase 53.6 — Reports ─────────────────────────────────────────────────
function ReportsTab() {
  const { isSuperAdmin } = useUsmAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(); monthStart.setDate(1);
  const [dateFrom, setDateFrom] = useState(monthStart.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoriesMap, setCategoriesMap] = useState({});

  useEffect(() => {
    if (isSuperAdmin) usmApi.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
    usmApi.get("/accounting/expense-categories").then((r) => {
      const m = {}; (r.data || []).forEach((c) => { m[c.code] = c.label; });
      setCategoriesMap(m);
    }).catch(() => {});
  }, [isSuperAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (isSuperAdmin && branchId) params.set("branch_id", branchId);
      const r = await usmApi.get(`/accounting/reports/summary?${params.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Rapor yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, branchId]);

  const exportExcel = () => {
    if (!data) return;
    const rows = [];
    rows.push(["Uzungöl Kuyumculuk — Rapor", `${dateFrom} → ${dateTo}`]);
    rows.push([]);
    rows.push(["Satış KPI"]);
    rows.push(["Ciro (TRY)", data.sales_kpi?.revenue || 0]);
    rows.push(["Satış Sayısı", data.sales_kpi?.count || 0]);
    rows.push([]);
    rows.push(["Para Birimi Bazlı Toplamlar"]);
    rows.push(["Para", "Gelir", "Gider", "Net"]);
    Object.entries(data.totals_by_currency || {}).forEach(([cur, v]) => {
      rows.push([cur, v.income, v.expense, (v.income - v.expense)]);
    });
    rows.push([]);
    rows.push(["Günlük Satış"]);
    rows.push(["Tarih", "Ciro", "Adet"]);
    (data.daily_sales || []).forEach((d) => rows.push([d.date, d.revenue, d.count]));
    rows.push([]);
    rows.push(["Gider Kategorileri"]);
    rows.push(["Kategori", "Toplam", "Adet"]);
    (data.expense_categories || []).forEach((c) => rows.push([categoriesMap[c.category] || c.category, c.total, c.count]));
    rows.push([]);
    rows.push(["Marka Bazlı Ciro"]);
    rows.push(["Marka", "Ciro", "Adet"]);
    (data.brands || []).forEach((b) => rows.push([b.brand_name, b.revenue, b.qty]));
    rows.push([]);
    rows.push(["Kasa Bakiyeleri"]);
    rows.push(["Hesap", "Para", "Bakiye"]);
    (data.cash_accounts || []).forEach((a) => rows.push([a.label, a.currency, a.balance]));
    rows.push([]);
    rows.push(["Banka Bakiyeleri"]);
    rows.push(["Banka", "Para", "Bakiye"]);
    (data.bank_accounts || []).forEach((a) => rows.push([a.label, a.currency, a.balance]));

    const csv = rows.map((r) => r.map((v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n;]/.test(s) ? `"${s}"` : s;
    }).join(";")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `rapor_${dateFrom}_${dateTo}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportPdf = () => window.print();

  return (
    <div data-testid="reports-tab" className="print:bg-white">
      <div className="flex flex-wrap items-end gap-3 mb-4 no-print">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Başlangıç</label>
          <input data-testid="reports-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 border border-[#E7E5E4] text-sm" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Bitiş</label>
          <input data-testid="reports-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 border border-[#E7E5E4] text-sm" />
        </div>
        {isSuperAdmin && (
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Şube</label>
            <select data-testid="reports-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="px-3 py-2 border border-[#E7E5E4] text-sm bg-white min-w-[200px]">
              <option value="">Tüm şubeler</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <button data-testid="reports-export-excel" onClick={exportExcel} className="inline-flex items-center gap-1.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] text-xs uppercase tracking-[0.2em] px-4 py-2">
            <Download size={14} /> Excel/CSV
          </button>
          <button data-testid="reports-export-pdf" onClick={exportPdf} className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-4 py-2">
            <Printer size={14} /> Yazdır / PDF
          </button>
        </div>
      </div>

      {loading ? <div className="py-16 text-center text-sm text-[#71717A]">Yükleniyor…</div> : !data ? (
        <p className="text-sm text-[#71717A]">Veri yok.</p>
      ) : (
        <div className="space-y-6 print:space-y-4">
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ReportKpi label="Satış Adedi" value={data.sales_kpi.count} />
            <ReportKpi label="Toplam Ciro (TRY)" value={fmt(data.sales_kpi.revenue, "TRY")} accent />
            <ReportKpi label="Toplam Gider (TRY)" value={fmt((data.totals_by_currency?.TRY?.expense || 0), "TRY")} />
            <ReportKpi label="Net (TRY)" value={fmt(((data.totals_by_currency?.TRY?.income || 0) - (data.totals_by_currency?.TRY?.expense || 0)), "TRY")} accent />
          </div>

          {/* Currency Totals */}
          <ReportSection title="Para Birimi Bazlı Gelir / Gider">
            <table className="min-w-full text-sm">
              <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                <tr><th className="text-left px-3 py-2">Para Birimi</th><th className="text-right px-3 py-2">Gelir</th><th className="text-right px-3 py-2">Gider</th><th className="text-right px-3 py-2">Net</th></tr>
              </thead>
              <tbody>
                {Object.entries(data.totals_by_currency || {}).length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-6 text-[#A8A29E] text-sm">Kayıt yok.</td></tr>
                ) : Object.entries(data.totals_by_currency || {}).map(([cur, v]) => (
                  <tr key={cur} data-testid={`report-cur-${cur}`} className="border-t border-[#F5F5F4]">
                    <td className="px-3 py-2 font-medium">{cur}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmt(v.income, cur)}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-700">{fmt(v.expense, cur)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(v.income - v.expense, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportSection>

          {/* Daily Sales */}
          <ReportSection title="Günlük Satış">
            {data.daily_sales?.length === 0 ? <p className="text-sm text-[#A8A29E] py-4 text-center">Satış yok.</p> : (
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                  <tr><th className="text-left px-3 py-2">Tarih</th><th className="text-right px-3 py-2">Ciro</th><th className="text-right px-3 py-2">Adet</th></tr>
                </thead>
                <tbody>
                  {(data.daily_sales || []).map((d) => (
                    <tr key={d.date} data-testid={`report-daily-${d.date}`} className="border-t border-[#F5F5F4]">
                      <td className="px-3 py-2 font-mono text-[11.5px]">{d.date}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(d.revenue, "TRY")}</td>
                      <td className="px-3 py-2 text-right font-mono">{d.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ReportSection>

          {/* Expense Categories */}
          <ReportSection title="Gider Kategorileri">
            {data.expense_categories?.length === 0 ? <p className="text-sm text-[#A8A29E] py-4 text-center">Gider kaydı yok.</p> : (
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                  <tr><th className="text-left px-3 py-2">Kategori</th><th className="text-right px-3 py-2">Toplam</th><th className="text-right px-3 py-2">Adet</th></tr>
                </thead>
                <tbody>
                  {(data.expense_categories || []).map((c) => (
                    <tr key={c.category} data-testid={`report-cat-${c.category}`} className="border-t border-[#F5F5F4]">
                      <td className="px-3 py-2">{categoriesMap[c.category] || c.category}</td>
                      <td className="px-3 py-2 text-right font-mono text-red-700">{fmt(c.total, "TRY")}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ReportSection>

          {/* Brand Revenue */}
          <ReportSection title="Marka Bazlı Ciro">
            {data.brands?.length === 0 ? <p className="text-sm text-[#A8A29E] py-4 text-center">Marka satışı yok.</p> : (
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                  <tr><th className="text-left px-3 py-2">Marka</th><th className="text-right px-3 py-2">Ciro</th><th className="text-right px-3 py-2">Adet</th></tr>
                </thead>
                <tbody>
                  {(data.brands || []).map((b) => (
                    <tr key={b.brand_id} data-testid={`report-brand-${b.brand_id}`} className="border-t border-[#F5F5F4]">
                      <td className="px-3 py-2">{b.brand_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(b.revenue, "TRY")}</td>
                      <td className="px-3 py-2 text-right font-mono">{b.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ReportSection>

          {/* Cash + Bank Balances */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ReportSection title="Kasa Bakiyeleri">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                  <tr><th className="text-left px-3 py-2">Hesap</th><th className="text-right px-3 py-2">Bakiye</th></tr>
                </thead>
                <tbody>
                  {(data.cash_accounts || []).map((a) => (
                    <tr key={a.id} data-testid={`report-cash-${a.code}`} className="border-t border-[#F5F5F4]">
                      <td className="px-3 py-2">{a.label}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(a.balance || 0, a.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ReportSection>
            <ReportSection title="Banka Bakiyeleri">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAFAF9] text-[10.5px] uppercase tracking-[0.15em] text-[#71717A]">
                  <tr><th className="text-left px-3 py-2">Banka</th><th className="text-right px-3 py-2">Bakiye</th></tr>
                </thead>
                <tbody>
                  {(data.bank_accounts || []).length === 0 ? (
                    <tr><td colSpan={2} className="text-center py-6 text-[#A8A29E] text-sm">Banka yok.</td></tr>
                  ) : (data.bank_accounts || []).map((a) => (
                    <tr key={a.id} data-testid={`report-bank-${a.code}`} className="border-t border-[#F5F5F4]">
                      <td className="px-3 py-2">{a.label}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(a.balance || 0, a.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ReportSection>
          </div>
        </div>
      )}
      <style>{`@media print { .no-print { display: none !important; } .print\\:bg-white { background: white; } .print\\:space-y-4 > * + * { margin-top: 1rem; } }`}</style>
    </div>
  );
}

function ReportKpi({ label, value, accent }) {
  return (
    <div className={"border p-4 " + (accent ? "bg-[#FBF5E8] border-[#E4CB88]" : "bg-white border-[#E7E5E4]")}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</p>
      <p className="font-finance text-xl mt-1">{value}</p>
    </div>
  );
}

function ReportSection({ title, children }) {
  return (
    <div>
      <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">{title}</h3>
      <div className="overflow-x-auto bg-white border border-[#E7E5E4]">{children}</div>
    </div>
  );
}
